import { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { API, Storage, graphqlOperation } from 'aws-amplify';
import { timezones } from "../../../constants/timezones";
import { getPrizeForConfig, getWinningMomentsCount } from '../../../graphql/queries';
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { GetPrizeForConfigResult, PrizeTableItem } from "../../../types/componentTypes/PrizeTypes";
import { AddPrizeState, emptyConfigType, PrizeState, PrizeStateDetailsProps, ImageMetadata } from "../../../types/componentTypes/prizeComponentTypes";
import { languageCodes } from '../../../constants/lists';
import { defaultNotifState } from '../../../constants/currency-constants'
import { isNotSameDate } from '../../../utils/dateUtils';

const uniqid = require('uniqid');


const emptyConfig = {
    configurationId: '',
    promotionId: '',
    configurationParameters: {
        configurationDatesTimezone: '',
        configurationStartUtc: '',
        configurationEndUtc: '',
        country: '',
        additionalInformation: {
            name: ''
        },
        language: ''
    },
    flow: {
        instantWin: {
            params: {
                winningLimitsPerPrize: {}
            }
        }
    }
}


const transformMapToArray = (values : {[key:string]: any}) => {
    const stateValues = ['name', 'desc', 'short_desc', 'redeem_desc'];
    stateValues.forEach((value:string ) => {
        const mapValue = JSON.parse(values[value]);
        const arrayValue = Object.keys(mapValue).map(key => ({[key]: mapValue[key]}));
        values[value] = arrayValue
    })
    return values;
}

const stringifyBooleans = (prize: PrizeTableItem) => {
    const stateValues = ['pool_prize', 'auto_redeem', 'voucher_dist', 'dynamic_gen'];
    stateValues.forEach((value: string) => {
        prize[value] = JSON.stringify(!!prize[value])
    })
}

const transformUTCtoTimezone = (configDate: string, timezoneString: string)  => {
    let date: string;
    if (configDate) {
        const utcDate = moment.utc(configDate).format();
        const timezone = Object.keys(timezones).find(key => timezones[key] === timezoneString) || 'Europe/London';
        date = moment.tz(utcDate, timezone).format();
    } else {
        date = moment().add(15, 'minutes').format('YYYY-MM-DD HH:mm')
    }

    return date
}

const prepareImgMetadataTempParams = (
        metadataStr: string|undefined,
        defaultLangCode: string,
        languages: string[],
        imgUrl: string[]
    ) => {
    let langUsingDefaultImgs: Record<string, boolean> = {}
    let imgMetadata: ImageMetadata|undefined = undefined
    const langCodeAndTheirImgs: Record<string, string[]> = {}
    if (metadataStr) {
        imgMetadata = JSON.parse(metadataStr)
        languages.forEach(langCode => {
            const metadata = imgMetadata[langCode]
            if (metadata && !(langCode in langCodeAndTheirImgs)) {
                langCodeAndTheirImgs[langCode] = metadata.map(obj => obj?.url)
            }
        });
        const defaultImgs = langCodeAndTheirImgs[defaultLangCode] || []
        for (const langCode of languages) {
            let value: boolean;
            if (langCode !== defaultLangCode && langCode in langCodeAndTheirImgs) {
                const langCodeImgs = langCodeAndTheirImgs[langCode]
                const usesDefaultImgs =
                    langCodeImgs.length === defaultImgs.length &&
                    langCodeImgs.every(img => defaultImgs.includes(img))
                if (!usesDefaultImgs) {
                    value = false
                } else {
                    value = true
                }
                langUsingDefaultImgs[langCode] = value
            }
        }
    } else {
            languages.forEach(langCode => {
                langCodeAndTheirImgs[langCode] = imgUrl
            })
        langUsingDefaultImgs = languages.reduce((acc, langCode) => {
            if (langCode !== defaultLangCode) {
                acc[langCode] = true
            }
            return acc
        }, {})
    }
    return { langUsingDefaultImgs, imgMetadata, imgUrls: langCodeAndTheirImgs }
}

/*
This function makes it so the default language is always at index 0.
This is needed because the prize page has language tabs and depending on the language tab index, its getting that index from each value's arr. And the array indexes coming from the db are inconsistent.
*/
const sortLanguages = (defaultLang: string, prize: AddPrizeState) => {
    const values = ['name', 'desc', 'short_desc', 'redeem_desc']
    values.forEach(val => {
        prize[val].sort((a, b) =>
            (Object.keys(a)[0] === defaultLang ? -1 : 1) - (Object.keys(b)[0] === defaultLang ? -1 : 1)
        )
    })
}

const useGetPrizeStateDetails = ({initialValues, state, configurationId, reset, setReset, setInitialState}: PrizeStateDetailsProps) => {
    const [notificationState, setNotificationState] = useState(defaultNotifState);
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [timelineLocked, setTimelineLocked] = useState(false);
    const [editState, setEditState] = useState(state?.prize);
    const [configData, setConfigData] = useState<emptyConfigType>(emptyConfig);
    const [tabLabels, setTabLabels] = useState<Array<string>>([]);
    const [defaultLang, setDefaultLang] = useState<string>();

    useEffect(() => {
        if (!configurationId) {
            setLoading(false);
            setDisabled(true);
        }
        if(state?.isEdit) {
            const hasWinningMoments = async (configurationId: string, prizeId: string) => {
                let nextToken: string | null = null;

                do {
                    const response: any = await API.graphql({
                        query: getWinningMomentsCount,
                        variables: { configurationId, prizeId, nextToken },
                    });
                    const winningMomentsData = response?.data?.getWinningMomentsCount;

                    if ((winningMomentsData?.count || 0) > 0) {
                        return true;
                    }

                    nextToken = winningMomentsData?.nextToken || null;
                } while (nextToken);

                return false;
            };

            const fetchPrize = async () => {
                try {
                    const response = await API.graphql(
                        graphqlOperation(getPrizeForConfig, { configuration_id: state.configData.configurationId, prize_id: state.prize.prize_id })
                    ) as GraphQLResult<GetPrizeForConfigResult>
                    delete response.data.getPrizeForConfig.__typename
                    return response.data.getPrizeForConfig;
                } catch (e) {
                    console.error(e);
                }
            };

            const modifyPrizeValues = (prize) => {
                stringifyBooleans(prize);
                if (!prize.tags) prize.tags = [];
                prize.barcode_type = prize.barcode_type ? JSON.stringify(prize.barcode_type) : '0';
                prize.delivery_type = prize.delivery_type ? JSON.stringify(prize.delivery_type) : '1';
                if (prize.cost) { prize.cost = JSON.parse(prize.cost) }
            }

            const loadPrizeForEdit = async () => {
                try {
                    const prize = await fetchPrize() as PrizeTableItem;
                    const imgUrl = typeof prize.img_url === 'string' ? [prize.img_url] : prize.img_url
                    const transformedPrize = { ...transformMapToArray(prize), redemption_limit: state.prize.redemption_limit } as PrizeState;
                    modifyPrizeValues(transformedPrize)
                    const prizeStartDate = transformUTCtoTimezone(transformedPrize.start_date as string, transformedPrize.prize_activity_timezone);
                    const prizeEndDate = transformUTCtoTimezone(transformedPrize.end_date as string, transformedPrize.prize_activity_timezone);
                    const configStartDate = transformUTCtoTimezone(state.prize.tempParams.start_date, state.prize.tempParams.config_timezone);
                    const configEndDate = transformUTCtoTimezone(state.prize.tempParams.end_date, state.prize.tempParams.config_timezone);

                    const promoParameters = state.configData.configurationParameters;

                    const promoStart = transformUTCtoTimezone(promoParameters.configurationStartUtc as string, promoParameters.configurationDatesTimezone);
                    const promoEnd = transformUTCtoTimezone(promoParameters.configurationEndUtc as string, promoParameters.configurationDatesTimezone);

                    const isInstantWin = !!state.configData.flow?.instantWin;
                    const hasPromotionStarted = Number(promoParameters.configurationStartUtc) <= Date.now();
                    const prizeHasWinningMoments = isInstantWin
                        ? await hasWinningMoments(state.configData.configurationId, transformedPrize.prize_id)
                        : false;

                    setTimelineLocked(isInstantWin && hasPromotionStarted && prizeHasWinningMoments);

                const prizeState: AddPrizeState = {
                    ...transformedPrize,
                    img_url: imgUrl,
                    tempParams: {
                        ...state.prize.tempParams,
                        prizeStatus: !prize.active ? 'inactive' : isNotSameDate(prizeStartDate, configStartDate) ? 'custom' : 'active',
                        promoStart: promoStart,
                        promoEnd: promoEnd
                    },
                    voucher_dist: prize.dynamic_gen === true ? '' : transformedPrize.voucher_dist,
                }
                if (!state.prize.tempParams.start_date && !state.prize.tempParams.end_date) {
                    prizeState.tempParams.start_date = (prizeState.start_date && prizeState.end_date) ? prizeStartDate : configStartDate;
                    prizeState.tempParams.end_date = (prizeState.start_date && prizeState.end_date) ? prizeEndDate : configEndDate;
                } else {
                    prizeState.tempParams.start_date = prizeStartDate;
                    prizeState.tempParams.end_date = prizeEndDate;
                    prizeState.tempParams.prizeStatus = (!moment(prizeStartDate).isSame(configStartDate)) ? 'custom' : 'active';
                }
                const languages: string[] = Object.keys(Object.assign({}, ...prizeState.name));
                const { langUsingDefaultImgs, imgMetadata, imgUrls } = prepareImgMetadataTempParams(
                    prize?.images_metadata as any as string|undefined,
                    state.configData.configurationParameters.language,
                    languages,
                    imgUrl
                )
                if (imgMetadata) {
                    prizeState.images_metadata = imgMetadata
                    prizeState.tempParams.langUsingDefaultImgs = langUsingDefaultImgs
                    prizeState.tempParams.img_url = imgUrls
                } else {
                    delete prizeState.images_metadata
                    prizeState.tempParams.img_url = {}
                }
                prizeState.tempParams.defaultLang = state.configData.configurationParameters.language
                const defaultLanguage: string = languageCodes[state.configData.configurationParameters.language || 'en']
                const initialLang: string[] = languages.map((lang: string) => {
                    return languageCodes[lang]
                })
                const tabs: string[] = Array.from(new Set(['+ ADD NEW LANGUAGE', defaultLanguage, ...initialLang]));

                sortLanguages(state.configData.configurationParameters.language, prizeState)
                setDefaultLang(state.configData.configurationParameters.language)
                setEditState(prizeState)
                setInitialState(prizeState);
                setConfigData(state.configData)
                setTabLabels(tabs);
                setLoading(false);
            } catch (err) {
                setNotificationState({
                    open:true,
                    title:'Prize edit failed',
                    content:'Prize not found or in wrong format',
                    level: "error"
                })
                console.error("Getting prize failed wtih", err);
                setLoading(false);
                setDisabled(true)
                }
            }

            loadPrizeForEdit()
        } else {
            const retrieveConfigData = async () => {
                try {
                    const configResult: any = await Storage.get(`${configurationId}/conf.txt`, { download: true, cacheControl: 'no-cache' });
                    const result = await configResult.Body.text();
                    return JSON.parse(result);
                } catch (err) {
                    console.error('Error getting promotion data', err);
                }
            }
            retrieveConfigData()
                .then(data => {
                    setConfigData(data);
                    const configStartDate = transformUTCtoTimezone(data.configurationParameters.configurationStartUtc, data.configurationParameters.configurationDatesTimezone);
                    const configEndDate = transformUTCtoTimezone(data.configurationParameters.configurationEndUtc, data.configurationParameters.configurationDatesTimezone);

                    const updatedState: AddPrizeState = {
                        ...initialValues,
                        configuration_id: data.configurationId,
                        prize_id: uniqid(),
                        start_date: configStartDate,
                        end_date: configEndDate,
                        prize_activity_timezone:  data.configurationParameters?.configurationDatesTimezone || '',
                        priority: data.flow?.redeemPincodeForCurrencies ? 1 : null,
                        tempParams: {
                            ...initialValues.tempParams,
                            start_date: configStartDate,
                            end_date: configEndDate,
                            promoStart: configStartDate,
                            promoEnd: configEndDate,
                            country: data.configurationParameters?.country || '',
                            mechanic: data?.flow?.redeemPincodeForCurrencies ? 'c&g' :  data?.flow?.autoRedeemCnG ? 'autoRedeemCnG' : 'iw',
                            defaultLang: data.configurationParameters?.language,
                        }
                    }
                    setInitialState(updatedState)
                    const initialLang = languageCodes[data.configurationParameters?.language] || 'English';
                    const tabs = ['+ ADD NEW LANGUAGE', initialLang];
                    setDefaultLang(data.configurationParameters?.language || 'en')
                    setTabLabels(tabs)
                    setLoading(false);
                    setReset(false);
                })
                .catch(e => {
                    setNotificationState({
                        open:true,
                        title:'Configuration not found',
                        content:'Supplied configuration id does not exist!',
                        level: "error"
                    })
                    console.error("Retrieval of promotion metadata failed with:", e);
                    setLoading(false);
                    setDisabled(true)
                })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, configurationId, reset, setTabLabels])

    return {
        notificationState,
        disabled,
        loading,
        timelineLocked,
        editState,
        configData,
        tabLabels,
        defaultLang,
        setNotificationState,
        setEditState,
        setTabLabels
    };

}

export default useGetPrizeStateDetails;
